import moment from "moment";
import { ToDo } from "../../Components/ToDo";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import { Step } from "../Step/Step";
import { Cash } from "../Step/Cash";
import { StepEvent, StepEventType } from "../Step/StepEvent";
import { StepType } from "../Step/StepType";
import { PackageFromCustomerStepData } from "./Models/PackageFromCustomerStepData";
import { InpostReturnInfo } from "./Components/InpostReturnInfo";
import { PackageFromCustomerStepState } from "./Models/PackageFromCustomerStepState";
import { TimeUtils } from "../../Utils/TimeUtils";
import { appendable } from "@tblabs/truffle/Core/appendable";
import { Line, Link, MultilineInput } from "@tblabs/truffle";
import { StringUtils } from "../../Utils/StringUtils";
import { Important } from "../../Components/Utils/Important";
import { CashType } from "../Step/CashType";


export class PackageFromCustomerStep extends Step<PackageFromCustomerStepState>
{
    public Type = StepType.PackageFromCustomer;
    public Icon = "📫";
    public TitleForCustomer = () => `Zwrot paczką`;
    public Title = () => `Zwrot paczką (${this.Data.Carrier.value || "Inpost"})`;

    public InitialState = PackageFromCustomerStepState.ReturnInfo;
    public WellDoneStates = [PackageFromCustomerStepState.PackageContentOk];
    public FailedDoneStates = [PackageFromCustomerStepState.NotDelivered, PackageFromCustomerStepState.PackageContentNotOk, PackageFromCustomerStepState.Cancelled];

    public get AsEvents(): StepEvent[]
    {
        return [
            new StepEvent(StepEventType.PackageFromCustomer, this.Order.Id, this.Icon, "Zwrot paczką", this.Data.PlannedDispatchTime.value, this.IsDone)
        ];
    }
    public get Mana(): Cash[]
    {
        const cash: Cash[] = [];

        if ([PackageFromCustomerStepState.Delivered, PackageFromCustomerStepState.PackageContentOk, PackageFromCustomerStepState.PackageContentNotOk].includes(this.Data.State.value as PackageFromCustomerStepState)
            && this.Data.ManaInPackage.value > 0)
        {
            cash.push(new Cash(CashType.ReceivedIncome, this, this.Data.ManaInPackage.value, "Gotówka w paczce", new Date(+this.Data.DeliveryTime.value || +this.Data.PlannedDispatchTime.value)));
        }

        if (this.Data.PackageCost.value > 0)
        {
            cash.push(new Cash(CashType.Expense, this, this.Data.PackageCost.value, "Koszt zwrotu paczki", new Date(+this.Data.DeliveryTime.value || +this.Data.PlannedDispatchTime.value)));
        }

        return cash;
    }
    public get WhenPlanned(): Date
    {
        return this.Data.PlannedDispatchTime.value;
    }
    public get WhenDone(): Date
    {
        return this.Data.DeliveryTime.value;
    }

    constructor(public Data: PackageFromCustomerStepData)
    {
        super(Data);

        if (!this.IsDone)
        {
            if (moment(this.Data.PlannedDispatchTime.value).isBefore(moment(DateTimeProvider.Now)))
                this.GoTo(PackageFromCustomerStepState.TimeToSend);
        }
    }

    private PackageSendDeclared(): void
    {
        this.Data.PackageSendDeclared.value = DateTimeProvider.Now;
        this.GoTo(PackageFromCustomerStepState.CustomerDeclaredPackageSent)
    }

    private ReturnInfoByCarrier(): appendable[]
    {
        const carrier = this.Data.Carrier.value.toLowerCase();

        if (["inpost", "", "paczkomat"].includes(carrier)) // "" due to legacy data
        {
            return [
                `Sprzęt należy odesłać Paczkomatem`,
                new InpostReturnInfo(this.Data),
                `Nadaj paczkę do dnia ${TimeUtils.FormattedDate(this.Data.PlannedDispatchTime)},\nmaksymalnie do godziny 12:00`,
                new Line(),
                new Link("Zobacz jak zapakować paczkę", "https://tutorial.specteam.pl/#slide/jak-odeslac-paczke").OpenInNewCard()
            ];
        }
        else if (["uber", "bolt", "taxi"].includes(carrier))
        {
            return [
                `Sprzęt należy odesłać taksówką na adres ustalony przez telefon.\n\nZabezpiecz dobrze przesyłkę oraz nie zapomnij wysłać informacji trakujących do odbiorcy`,
            ];
        }

        return ["Forma zwrotu nie została określona. Skontaktuj się z nami aby naprawić ten błąd."];
    }

    public STATES = {
        [PackageFromCustomerStepState.ReturnInfo]: {
            StateName: "Informacje o zwrocie",
            Timeout: () => this.Data.PlannedDispatchTime,
            Customer: {
                Content: () => [...this.ReturnInfoByCarrier()]
            },
            Admin: {
                Content: () => [
                    `Zwrot z użyciem ${this.Data.Carrier.value} (dostępne opcje: inpost/uber/taxi).\n\nKlient (${StringUtils.FormatName(this.Order.People.First()?.Name?.value)}) widzi informacje o zwrocie.`,
                    new ToDo(`Można wysłać przypomnienie o zwrocie`),
                ],
                Actions: {
                    "🏠": () => { this.Data.DeliveryTime.value = DateTimeProvider.Now; this.GoTo(PackageFromCustomerStepState.Delivered); },
                    "✅": () => this.PackageSendDeclared(),
                    "❌": () => this.GoTo(PackageFromCustomerStepState.NotDelivered),
                },
            },
        },
        [PackageFromCustomerStepState.TimeToSend]: {
            StateName: "Pora na odesłanie sprzętu",
            Timeout: () => this.Data.PlannedDispatchTime,
            Customer: {
                Content: () => [
                    moment(this.Data.PlannedDispatchTime.value).isBefore(moment(DateTimeProvider.Now)) && new Important(`Czas na odesłanie sprzętu minął ${TimeUtils.TimeLeftTo(this.Data.PlannedDispatchTime)}.\nJeśli paczka jeszcze nie została odesłana - najwyższa pora to zrobić!\nJeśli chcesz przedłużyć rezerwację - daj nam znać telefonicznie`),
                    ...this.ReturnInfoByCarrier(),
                ]
            },
            Admin: {
                Content: () => [
                    `Czas na odesłanie sprzętu minął ${TimeUtils.TimeLeftTo(this.Data.PlannedDispatchTime)}.`,
                    new ToDo(`Upewnij się, że klient odesłał sprzęt`),
                ],
                Actions: {
                    "🏠": () => { this.Data.DeliveryTime.value = DateTimeProvider.Now; this.GoTo(PackageFromCustomerStepState.Delivered); },
                    "✅": () => this.PackageSendDeclared(),
                    "❌": () => this.GoTo(PackageFromCustomerStepState.NotDelivered),
                },
            },
        },
        [PackageFromCustomerStepState.CustomerDeclaredPackageSent]: {
            StateName: "Zadeklarowano nadanie paczki",
            Timeout: () => moment(this.Data.PackageSendDeclared.value).add(3, "days").toDate(),
            Customer: {
                Content: () => [`☑️ Zadeklarowano wysłanie paczki`],
            },
            Admin: {
                Content: () => [
                    `☑️ Klient zadeklarował nadanie paczki`,
                    new ToDo(`Sprawdź status paczki w aplikacji przewoźnika o ile takowy jest dostępny`)
                ],
                Actions: {
                    "✅": () => this.GoTo(PackageFromCustomerStepState.OnTheWay),
                    "❌": () => this.GoTo(PackageFromCustomerStepState.NotDelivered),
                }
            }
        },
        [PackageFromCustomerStepState.OnTheWay]: {
            StateName: "Paczka w drodze",
            Timeout: () => moment(this.Data.PackageSendDeclared.value).add(4, "days").toDate(),
            Customer: {
                Content: () => [`🚚 Paczka w drodze`],
            },
            Admin: {
                Content: () => [
                    `☑️ Paczka od ${StringUtils.FormatName(this.Order.People.First()?.Name?.value)} w drodze od ${TimeUtils.FormattedDate(this.Data.PackageSendDeclared)}.\nStatus jest widoczny w aplikacji przewoźnika.`,
                    new ToDo(`Czy paczka dotarła? Jeśli mineło więcej niż 5 dni kliknij ❌ i wyjaśnij sprawę`)
                ],
                Actions: {
                    "✅": () => { this.Data.DeliveryTime.value = DateTimeProvider.Now; this.GoTo(PackageFromCustomerStepState.Delivered); },
                    "❌": () => this.GoTo(PackageFromCustomerStepState.NotDelivered),
                }
            }
        },
        [PackageFromCustomerStepState.Delivered]: {
            StateName: "Dostarczona",
            Customer: {
                Content: () => [`✅ Paczka dostarczona`],
            },
            Admin: {
                Content: () => [
                    `✅ Paczka odebrana`,
                    this.Data.ManaInPackage.value > 0 && `\nW paczce powinno być dodatkowo ${this.Data.ManaInPackage.value} zł`,
                    new ToDo(`Czy zawartość jest ok?`,
                        new MultilineInput(this.Data.PackageContentNotOkInfo).Rows(4).Placeholder("Opis problemu lub brakującego sprzętu. Widoczny dla klienta!"))],
                Actions: {
                    "✅": () => { this.GoTo(PackageFromCustomerStepState.PackageContentOk); },
                    "❌": () => { this.GoTo(PackageFromCustomerStepState.PackageContentNotOk); },
                }
            }
        },
        [PackageFromCustomerStepState.PackageContentOk]: {
            StateName: "Zawartość paczki jest ok",
            Customer: {
                Content: () => [`✅ Paczka dostarczona. Zawartość jest ok`],
            },
            Admin: {
                Content: () => [`✅ Paczka odebrana. Zawartość jest ok`],
            }
        },
        [PackageFromCustomerStepState.NotDelivered]: {
            StateName: "Paczka nie dotarła",
            Customer: {
                Content: () => [`❌ Paczka nie dotarła. Jako jej nadawca skontaktuj się z przewoźnikiem i złóż reklamację`],
            },
            Admin: {
                Content: () => [
                    `❌ Paczka nie dotarła`,
                    new ToDo("Daj znać klientowi, że ma zadzwonić do przewoźnika i złożyć reklamacje, bo tylko on ma uprawnienia do zarządzania własną paczką")],
            }
        },
        [PackageFromCustomerStepState.PackageContentNotOk]: {
            StateName: "Zawartość paczki jest nieprawidłowa",
            Customer: {
                Content: () => [`❌ Paczka dostarczona, ale jest problem z jej zawartością:\n${this.Data.PackageContentNotOkInfo.value || "(brak notatki)"}`],
            },
            Admin: {
                Content: () => [`❌ Paczka dostarczona, ale jest problem z jej zawartością:\n${this.Data.PackageContentNotOkInfo.value || "brak informacji"}`,
                new ToDo("Zadzwoń do klienta i powiedz mu co jest nie tak. Być może trzeba będzie coś dosłać (i warto dodać wtedy krok z kolejną paczką) albo coś zapłacić (i zaznaczyć to w zwrocie kaucji, że suma jest mniejsza)")
                ],
            }
        },
        [PackageFromCustomerStepState.Cancelled]: {
            StateName: "Odwołane",
            Customer: {
                Content: () => [`✖️ Zwrot został odwołany`],
            },
            Admin: {
                Content: () => [`✖️ Zwrot został odwołany. Najpewniej dlatego, że paczka nie dotarła do klienta bądź została zwrócona`],
            }
        },
    };
}
